import Dexie from 'dexie';
import { db } from '../db/db';

export type LogLevel = 'info' | 'warn' | 'error' | 'debug';

function getContext(): string {
  if (typeof window === 'undefined') return '[Unknown]';
  if (window.location.protocol === 'chrome-extension:') {
    if (window.location.pathname.includes('offscreen')) return '[Offscreen]';
    if (window.location.pathname.includes('background') || !window.document) return '[SW]';
    if (window.location.pathname.includes('options')) return '[Options]';
    return '[Popup/UI]';
  }
  return '[Web]';
}

function formatArgs(args: any[]): string {
    return args.map(arg => {
        if (typeof arg === 'object') {
            try {
                // Handle Errors explicitly because JSON.stringify(new Error('...')) often returns "{}"
                if (arg instanceof Error) {
                    return `${arg.name}: ${arg.message}\n${arg.stack}`;
                }
                return JSON.stringify(arg);
            } catch (e) {
                return '[Unserializable Object]';
            }
        }
        return String(arg);
    }).join(' ');
}

const LOG_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000; // 7 days
const LOG_PRUNE_INTERVAL_MS = 60 * 60 * 1000; // 1 hour
// Hard cap: keep at most this many rows regardless of age. Without a cap the
// table can reach hundreds of thousands of rows in a week of active use, which
// slows backup (chunkedExportDB reads every row) and IndexedDB queries.
const LOG_MAX_COUNT = 10_000;
let lastLogPrune = 0;

async function persistLog(level: LogLevel, args: any[]) {
    const timestamp = Date.now();
    const context = getContext();
    const message = formatArgs(args);
    const memoryUsage = typeof performance !== 'undefined' ? (performance as any).memory?.usedJSHeapSize : undefined;

    // Dexie.ignoreTransaction creates an independent transaction context, so this
    // write is safe even when called from within an active Dexie transaction.
    // We do NOT defer via setTimeout here: deferring meant log entries were lost
    // when the service worker was killed before the deferred callback fired, which
    // caused crashes to produce no informative log output.
    try {
        await Dexie.ignoreTransaction(async () => {
            await db.logs.add({
                level,
                message,
                context,
                timestamp,
                timestampString: new Date(timestamp).toLocaleString(),
                memoryUsage,
            });

            const pruneNow = Date.now();
            if (pruneNow - lastLogPrune > LOG_PRUNE_INTERVAL_MS) {
                lastLogPrune = pruneNow;
                await db.logs.where('timestamp').below(pruneNow - LOG_MAX_AGE_MS).delete();
                // Hard count cap: if the table still exceeds LOG_MAX_COUNT after the
                // age-based prune, delete the oldest entries to bring it under the cap.
                const count = await db.logs.count();
                if (count > LOG_MAX_COUNT) {
                    const excess = count - LOG_MAX_COUNT;
                    const oldestIds = await db.logs.orderBy('timestamp').limit(excess).primaryKeys() as number[];
                    const BATCH = 200;
                    for (let i = 0; i < oldestIds.length; i += BATCH) {
                        await db.logs.bulkDelete(oldestIds.slice(i, i + BATCH));
                    }
                }
            }
        });
    } catch (e) {
        // Dexie rejects rw transactions when called from a liveQuery context (PSD.subscr is set).
        // Escape to the next macrotask, which is outside the Dexie zone, and retry.
        if (e instanceof Error && e.message.includes('liveQuery')) {
            setTimeout(() => persistLog(level, args), 0);
            return;
        }
        const eStr = e instanceof Error ? `${e.name}: ${e.message}\n${e.stack}` : JSON.stringify(e);
        console.error(`Failed to write to persistent log DB: ${eStr}\nOriginal message (${level}): ${message}`);
    }
}

export const logger = {
    info: (...args: any[]) => {
        console.info(...args);
        persistLog('info', args);
    },
    warn: (...args: any[]) => {
        console.warn(...args);
        persistLog('warn', args);
    },
    error: (...args: any[]) => {
        console.error(...args);
        persistLog('error', args);
    },
    debug: (...args: any[]) => {
        console.debug(...args);
        // Debug messages are not persisted to db.logs — they're too chatty and
        // accumulate thousands of rows per hour in hot paths.
    }
};

// Global handlers for unhandled errors
if (typeof window !== 'undefined') {
    window.addEventListener('error', (event) => {
        logger.error('Uncaught Error:', event.error || event.message);
    });

    window.addEventListener('unhandledrejection', (event) => {
        logger.error('Unhandled Promise Rejection:', event.reason);
    });
}
